import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { 
  Mail, Calendar, User, Trash2, Loader2, Inbox,
  ExternalLink, Clock, DollarSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CaseFiledEmails = ({ caseId }) => {
  const [filedEmails, setFiledEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const getToken = () => localStorage.getItem('token');

  const fetchFiledEmails = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/email-integration/filed-emails/${caseId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFiledEmails(data.filed_emails || []);
      }
    } catch (error) {
      console.error('Error fetching filed emails:', error);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchFiledEmails();
  }, [fetchFiledEmails]);

  const handleRemove = async (filedId) => {
    setDeleting(filedId);
    try {
      const response = await fetch(`${API_URL}/api/email-integration/filed-emails/${filedId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        toast.success('Email reference removed');
        setFiledEmails(filedEmails.filter(e => e.id !== filedId));
      } else {
        toast.error('Failed to remove email');
      }
    } catch (error) {
      toast.error('Failed to remove email');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (filedEmails.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center h-48 text-gray-500">
          <Mail className="w-12 h-12 mb-3 opacity-30" />
          <p className="text-sm">No emails filed to this case yet</p>
          <p className="text-xs mt-1">File emails from Settings → Email tab</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-500" />
            Filed Emails
            <Badge variant="secondary">{filedEmails.length}</Badge>
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {filedEmails.map((filed) => (
            <div
              key={filed.id}
              className="flex items-start justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium truncate">
                    Email ID: {filed.email_id?.slice(0, 20)}...
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Filed: {formatDate(filed.filed_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    By: {filed.filed_by}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => handleRemove(filed.id)}
                disabled={deleting === filed.id}
              >
                {deleting === filed.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default CaseFiledEmails;
